// ===========================================
// Edit Scope Modal (cross-term classes)
// ===========================================
// Google-Calendar-style "This event / This and following" for a class that spans multiple
// terms. Shown only when the class has 2+ term occurrences; lets the admin choose which terms
// an edit touches. LOCKED terms (the next term has started, no admin reopen is live) are NEVER
// touched - the read-only gate keeps history immutable. A term that has merely ended is still
// editable until the next term starts. The page skips this modal when no existing unlocked row
// would be written (an add-only edit of an all-locked class).
//
// Deliberately TWO options, not three. An "All terms" option was removed as redundant: 'all' =
// every UNLOCKED term, 'thisAndFuture' = every unlocked term starting at/after the anchor, so
// they differ only when an unlocked term starts BEFORE the anchor. Terms don't overlap and at
// most one is current, so the sole case was editing from a future term while an earlier term is
// still running — where "All terms" would silently rewrite a running term the admin wasn't
// looking at. Forward-only is the safer, clearer contract. The 'all' scope remains in EditScope
// and filterTermsByScope: LessonsPage still passes it directly for single-term edits and creates
// (no modal shown), where "touch every term" is exactly right.

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
} from '@mui/material';
import type { EditScope } from './lessons.helpers';

export type { EditScope };

export interface EditScopeModalProps {
  open: boolean;
  /** Name of the term the admin is currently viewing (for the "This term only" label). */
  currentTermName: string;
  loading?: boolean;
  onConfirm: (scope: EditScope) => void;
  onCancel: () => void;
}

const OPTIONS: { value: EditScope; label: (currentTermName: string) => string; help: string }[] = [
  {
    value: 'this',
    label: (t) => `This term only (${t})`,
    help: 'Change only this term’s class. Other terms are left as they are.',
  },
  {
    value: 'thisAndFuture',
    label: () => 'This and future terms',
    help: 'Change this term and every later term. Past terms stay unchanged.',
  },
];

export default function EditScopeModal({
  open,
  currentTermName,
  loading = false,
  onConfirm,
  onCancel,
}: EditScopeModalProps) {
  const [scope, setScope] = useState<EditScope>('this');

  // Reset to the least-destructive default each time the modal opens — each edit is a fresh
  // decision; a sticky prior selection (e.g. 'all') would be a surprising default.
  useEffect(() => {
    if (open) setScope('this');
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Apply this change to…</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This class runs across several terms. Choose which terms this edit should update.
        </Typography>
        <FormControl>
          <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as EditScope)}>
            {OPTIONS.map((opt) => (
              <Box key={opt.value} sx={{ mb: 1 }}>
                <FormControlLabel
                  value={opt.value}
                  control={<Radio />}
                  label={opt.label(currentTermName)}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                  {opt.help}
                </Typography>
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(scope)} variant="contained" disabled={loading}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
